<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import {
  PlusIcon,
  TrashIcon,
  TruckIcon,
  UserGroupIcon,
} from "@heroicons/vue/24/outline";
import ProductService from "@/services/ProductService";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Unit Management");

const productService = new ProductService();
const products = ref([]);
const skills = ref([]);
const vehicleTypes = ref([]);
const selectedProductId = ref(null);
const creating = ref(false);
const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const search = ref("");
const message = ref("");
const messageTone = ref("success");
const newPersonnelTypeName = ref("");
const savingPersonnelTypeId = ref(null);
const newVehicleTypeName = ref("");
const savingVehicleTypeId = ref(null);

const form = reactive({
  name: "",
  short: "",
  description: "",
  is_optional_extra: false,
  personnel_requirements: [],
  vehicle_enabled: false,
  vehicle_type_id: "",
  vehicle_count: 1,
});

const sortedProducts = computed(() =>
  [...products.value].sort((left, right) =>
    (left.name ?? "").localeCompare(right.name ?? ""),
  ),
);

const filteredProducts = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return sortedProducts.value;
  return sortedProducts.value.filter((product) =>
    `${product.name ?? ""} ${product.short ?? ""} ${product.description ?? ""}`
      .toLowerCase()
      .includes(query),
  );
});

const setMessage = (value, tone = "success") => {
  message.value = value;
  messageTone.value = tone;
};

const clearForm = () => {
  Object.assign(form, {
    name: "",
    short: "",
    description: "",
    is_optional_extra: false,
    personnel_requirements: [],
    vehicle_enabled: false,
    vehicle_type_id: "",
    vehicle_count: 1,
  });
};

const selectProduct = (product) => {
  selectedProductId.value = product.id;
  creating.value = false;
  message.value = "";
  const vehicle =
    product.vehicle_requirements ?? product.vehicleRequirements ?? null;
  Object.assign(form, {
    name: product.name ?? "",
    short: product.short ?? "",
    description: product.description ?? "",
    is_optional_extra: Boolean(product.is_optional_extra),
    personnel_requirements: (product.personnel_requirements ?? []).map(
      (requirement) => ({
        id: requirement.id,
        staff_skill_id: requirement.staff_skill_id,
        required_count: Number(requirement.required_count ?? 1),
      }),
    ),
    vehicle_enabled: Boolean(vehicle),
    vehicle_type_id: vehicle?.vehicle_type_id ?? "",
    vehicle_count: Number(vehicle?.required_count ?? 1),
  });
};

const startCreating = () => {
  clearForm();
  selectedProductId.value = null;
  creating.value = true;
  message.value = "";
};

const addPersonnelRequirement = () => {
  const usedIds = new Set(
    form.personnel_requirements.map((row) => Number(row.staff_skill_id)),
  );
  const skill = skills.value.find((entry) => !usedIds.has(Number(entry.id)));
  form.personnel_requirements.push({
    id: null,
    staff_skill_id: skill?.id ?? "",
    required_count: 1,
  });
};

const removePersonnelRequirement = (index) => {
  form.personnel_requirements.splice(index, 1);
};

const toggleVehicleRequirement = () => {
  if (!form.vehicle_enabled) {
    form.vehicle_type_id = "";
    form.vehicle_count = 1;
  }
};

const skillIsUsedElsewhere = (skillId, rowIndex) =>
  form.personnel_requirements.some(
    (row, index) =>
      index !== rowIndex && Number(row.staff_skill_id) === Number(skillId),
  );

const validationMessage = () => {
  if (!form.name.trim()) return "Enter a unit name.";
  if (!form.short.trim()) return "Enter a short code.";
  if (form.personnel_requirements.some((row) => !row.staff_skill_id)) {
    return "Choose a skill for each personnel requirement.";
  }
  if (
    form.personnel_requirements.some((row) => Number(row.required_count) < 1)
  ) {
    return "Personnel quantities must be at least 1.";
  }
  if (form.vehicle_enabled && !form.vehicle_type_id) {
    return "Choose a vehicle type.";
  }
  if (form.vehicle_enabled && Number(form.vehicle_count) < 1) {
    return "The vehicle quantity must be at least 1.";
  }
  return null;
};

const payload = () => ({
  name: form.name.trim(),
  short: form.short.trim(),
  description: form.description.trim() || null,
  is_optional_extra: form.is_optional_extra,
  personnel_requirements: form.personnel_requirements.map((requirement) => ({
    id: requirement.id || null,
    staff_skill_id: Number(requirement.staff_skill_id),
    required_count: Number(requirement.required_count),
  })),
  vehicle_requirement: form.vehicle_enabled
    ? {
        vehicle_type_id: Number(form.vehicle_type_id),
        required_count: Number(form.vehicle_count),
      }
    : null,
});

const describeError = (error, fallback) => {
  const errors = error.response?.data?.errors;
  if (errors) {
    const first = Object.values(errors).flat()[0];
    if (first) return first;
  }
  return error.response?.data?.message ?? fallback;
};

const save = async () => {
  const invalid = validationMessage();
  if (invalid) {
    setMessage(invalid, "error");
    return;
  }

  saving.value = true;
  message.value = "";
  try {
    const wasCreating = creating.value;
    const saved = wasCreating
      ? await productService.create(payload())
      : await productService.update(selectedProductId.value, payload());
    const index = products.value.findIndex(
      (product) => product.id === saved.id,
    );
    if (index === -1) products.value.push(saved);
    else products.value[index] = saved;
    selectProduct(saved);
    setMessage(wasCreating ? "Unit created." : "Unit saved.");
  } catch (error) {
    setMessage(describeError(error, "Unable to save this unit."), "error");
  } finally {
    saving.value = false;
  }
};

const deleteProduct = async () => {
  const product = products.value.find(
    (entry) => entry.id === selectedProductId.value,
  );
  if (
    !product ||
    !window.confirm(
      `Delete ${product.name}? This will archive the unit so it can no longer be selected.`,
    )
  ) {
    return;
  }

  deleting.value = true;
  message.value = "";
  try {
    await productService.delete(product.id);
    products.value = products.value.filter((entry) => entry.id !== product.id);
    const next = sortedProducts.value[0];
    if (next) selectProduct(next);
    else startCreating();
    setMessage("Unit deleted.");
  } catch (error) {
    setMessage(describeError(error, "Unable to delete this unit."), "error");
  } finally {
    deleting.value = false;
  }
};

const addPersonnelType = async () => {
  const name = newPersonnelTypeName.value.trim();
  if (!name) {
    setMessage("Enter a personnel type name.", "error");
    return;
  }

  savingPersonnelTypeId.value = "new";
  try {
    const created = await productService.createPersonnelType({ name });
    skills.value.push({ ...created, editing_name: created.name });
    skills.value.sort((left, right) => left.name.localeCompare(right.name));
    newPersonnelTypeName.value = "";
    setMessage("Personnel type added.");
  } catch (error) {
    setMessage(
      describeError(error, "Unable to add the personnel type."),
      "error",
    );
  } finally {
    savingPersonnelTypeId.value = null;
  }
};

const savePersonnelType = async (personnelType) => {
  const name = String(personnelType.editing_name ?? "").trim();
  if (!name) {
    setMessage("Personnel type names cannot be empty.", "error");
    return;
  }

  savingPersonnelTypeId.value = personnelType.id;
  try {
    const updated = await productService.updatePersonnelType(personnelType.id, {
      name,
    });
    Object.assign(personnelType, updated, { editing_name: updated.name });
    skills.value.sort((left, right) => left.name.localeCompare(right.name));
    setMessage("Personnel type saved.");
  } catch (error) {
    setMessage(
      describeError(error, "Unable to save the personnel type."),
      "error",
    );
  } finally {
    savingPersonnelTypeId.value = null;
  }
};

const addVehicleType = async () => {
  const name = newVehicleTypeName.value.trim();
  if (!name) {
    setMessage("Enter a vehicle type name.", "error");
    return;
  }

  savingVehicleTypeId.value = "new";
  try {
    const created = await productService.createVehicleType({
      name,
      active: true,
    });
    vehicleTypes.value.push({ ...created, editing_name: created.name });
    newVehicleTypeName.value = "";
    setMessage("Vehicle type added.");
  } catch (error) {
    setMessage(
      describeError(error, "Unable to add the vehicle type."),
      "error",
    );
  } finally {
    savingVehicleTypeId.value = null;
  }
};

const saveVehicleType = async (vehicleType) => {
  const name = String(vehicleType.editing_name ?? "").trim();
  if (!name) {
    setMessage("Vehicle type names cannot be empty.", "error");
    return;
  }

  savingVehicleTypeId.value = vehicleType.id;
  try {
    const updated = await productService.updateVehicleType(vehicleType.id, {
      name,
      active: Boolean(vehicleType.active),
    });
    Object.assign(vehicleType, updated, { editing_name: updated.name });
    setMessage("Vehicle type saved.");
    const selected = products.value.find(
      (product) => product.id === selectedProductId.value,
    );
    if (selected?.vehicle_requirements?.vehicle_type_id === updated.id) {
      selected.vehicle_requirements.vehicle_type = updated.name;
    }
  } catch (error) {
    setMessage(
      describeError(error, "Unable to save the vehicle type."),
      "error",
    );
  } finally {
    savingVehicleTypeId.value = null;
  }
};

const load = async () => {
  loading.value = true;
  try {
    const response = await productService.managementIndex();
    products.value = response.products ?? [];
    skills.value = (response.skills ?? []).map((skill) => ({
      ...skill,
      editing_name: skill.name,
    }));
    vehicleTypes.value = (response.vehicle_types ?? []).map((vehicleType) => ({
      ...vehicleType,
      editing_name: vehicleType.name,
    }));
    const first = sortedProducts.value[0];
    if (first) selectProduct(first);
    else startCreating();
  } catch (error) {
    setMessage(describeError(error, "Unable to load units."), "error");
  } finally {
    loading.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.2em] text-slate-400">
          Super User
        </p>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Unit Management
        </h1>
        <p class="mt-2 text-sm text-slate-500 dark:text-slate-300">
          Define products and the personnel and vehicles required for one unit.
          Prices remain in Pricing.
        </p>
      </div>
      <div class="flex gap-2">
        <router-link to="/pricing" class="btn h-10 px-4"
          >Open Pricing</router-link
        >
        <button
          type="button"
          class="btn accent inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap px-4"
          @click="startCreating"
        >
          <PlusIcon class="h-4 w-4" /> New Unit
        </button>
      </div>
    </div>

    <div
      v-if="message"
      class="rounded-lg border px-4 py-3 text-sm"
      :class="
        messageTone === 'error'
          ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-200'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200'
      "
    >
      {{ message }}
    </div>

    <div class="grid gap-6 lg:grid-cols-[18rem,minmax(0,1fr)]">
      <aside
        class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <input
          v-model="search"
          type="search"
          placeholder="Search units"
          class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
        />
        <div v-if="loading" class="py-8 text-center text-sm text-slate-400">
          Loading units...
        </div>
        <div v-else class="mt-3 max-h-[70vh] space-y-1 overflow-y-auto">
          <button
            v-for="product in filteredProducts"
            :key="product.id"
            type="button"
            class="w-full rounded-lg px-3 py-2 text-left transition"
            :class="
              selectedProductId === product.id && !creating
                ? 'bg-cyan-50 text-cyan-800 dark:bg-cyan-500/10 dark:text-cyan-200'
                : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700'
            "
            @click="selectProduct(product)"
          >
            <span class="block text-sm font-semibold">{{ product.name }}</span>
            <span class="text-xs text-slate-400">{{ product.short }}</span>
          </button>
          <p
            v-if="filteredProducts.length === 0"
            class="py-8 text-center text-sm text-slate-400"
          >
            No units found.
          </p>
        </div>
      </aside>

      <main
        class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:p-6"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4 dark:border-slate-700"
        >
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-400">
              {{ creating ? "New unit" : "Selected unit" }}
            </p>
            <h2
              class="text-xl font-semibold text-slate-900 dark:text-slate-100"
            >
              {{ creating ? "Create a unit" : form.name }}
            </h2>
          </div>
          <div class="flex gap-2">
            <button
              v-if="!creating"
              type="button"
              class="btn danger inline-flex h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap px-3"
              :disabled="deleting || saving"
              @click="deleteProduct"
            >
              <TrashIcon class="h-4 w-4" />
              {{ deleting ? "Deleting..." : "Delete" }}
            </button>
            <button
              type="button"
              class="btn accent h-9 px-4"
              :disabled="saving || deleting"
              @click="save"
            >
              {{
                saving ? "Saving..." : creating ? "Create Unit" : "Save Changes"
              }}
            </button>
          </div>
        </div>

        <section class="mt-5 grid gap-4 md:grid-cols-2">
          <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
            Unit name
            <input
              v-model="form.name"
              type="text"
              class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-normal dark:border-slate-600 dark:bg-slate-700"
            />
          </label>
          <label class="text-sm font-medium text-slate-700 dark:text-slate-200">
            Short code
            <input
              v-model="form.short"
              type="text"
              class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-normal dark:border-slate-600 dark:bg-slate-700"
            />
          </label>
          <label
            class="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200 md:col-span-2"
          >
            <input
              v-model="form.is_optional_extra"
              type="checkbox"
              class="h-4 w-4 rounded border-slate-300 text-cyan-600"
            />
            Make this unit available as an optional quote extra
          </label>
          <label
            class="text-sm font-medium text-slate-700 dark:text-slate-200 md:col-span-2"
          >
            Description
            <textarea
              v-model="form.description"
              rows="4"
              maxlength="2000"
              placeholder="Describe what this unit includes and its operational capability."
              class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-normal dark:border-slate-600 dark:bg-slate-700"
            ></textarea>
            <span
              class="mt-1 block text-xs font-normal text-slate-500 dark:text-slate-300"
            >
              Optional. This is given to the quote AI and shown on customer
              quotes whenever the unit is selected.
            </span>
          </label>
        </section>

        <section class="mt-8">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <UserGroupIcon class="h-5 w-5 text-cyan-600" />
              <div>
                <h3 class="font-semibold text-slate-900 dark:text-slate-100">
                  Personnel
                </h3>
                <p class="text-xs text-slate-500 dark:text-slate-300">
                  Required skills and headcount per unit.
                </p>
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <a
                href="#personnel-types"
                class="btn inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap px-3"
              >
                Manage Types
              </a>
              <button
                type="button"
                class="btn inline-flex h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap px-3"
                :disabled="form.personnel_requirements.length >= skills.length"
                @click="addPersonnelRequirement"
              >
                <PlusIcon class="h-4 w-4" /> Add Personnel
              </button>
            </div>
          </div>

          <div class="mt-4 space-y-3">
            <div
              v-for="(requirement, index) in form.personnel_requirements"
              :key="requirement.id ?? `new-${index}`"
              class="grid gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700 sm:grid-cols-[minmax(0,1fr),8rem,2.5rem]"
            >
              <select
                v-model="requirement.staff_skill_id"
                class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700"
              >
                <option value="" disabled>Select a skill</option>
                <option
                  v-for="skill in skills"
                  :key="skill.id"
                  :value="skill.id"
                  :disabled="skillIsUsedElsewhere(skill.id, index)"
                >
                  {{ skill.name }}
                </option>
              </select>
              <label class="flex items-center gap-2 text-xs text-slate-500">
                Qty
                <input
                  v-model.number="requirement.required_count"
                  type="number"
                  min="1"
                  max="999"
                  class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                />
              </label>
              <button
                type="button"
                class="btn danger inline-flex h-9 w-9 shrink-0 items-center justify-center p-0"
                title="Remove personnel requirement"
                @click="removePersonnelRequirement(index)"
              >
                <TrashIcon class="h-4 w-4" />
              </button>
            </div>
            <p
              v-if="form.personnel_requirements.length === 0"
              class="rounded-lg bg-slate-50 px-4 py-5 text-center text-sm text-slate-400 dark:bg-slate-700/50"
            >
              No personnel required for this unit.
            </p>
          </div>
        </section>

        <section
          class="mt-8 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <TruckIcon class="h-5 w-5 text-cyan-600" />
              <div>
                <h3 class="font-semibold text-slate-900 dark:text-slate-100">
                  Vehicle
                </h3>
                <p class="text-xs text-slate-500 dark:text-slate-300">
                  Vehicle type and quantity required per unit.
                </p>
              </div>
            </div>
            <label
              class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                v-model="form.vehicle_enabled"
                type="checkbox"
                class="h-4 w-4 rounded border-slate-300 text-cyan-600"
                @change="toggleVehicleRequirement"
              />
              Vehicle required
            </label>
          </div>
          <div
            v-if="form.vehicle_enabled"
            class="mt-4 grid gap-4 sm:grid-cols-[minmax(0,1fr),8rem]"
          >
            <label class="text-sm text-slate-600 dark:text-slate-300">
              Vehicle type
              <select
                v-model="form.vehicle_type_id"
                class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
              >
                <option value="" disabled>Select a vehicle type</option>
                <option
                  v-for="vehicleType in vehicleTypes"
                  :key="vehicleType.id"
                  :value="vehicleType.id"
                  :disabled="
                    !vehicleType.active &&
                    Number(form.vehicle_type_id) !== vehicleType.id
                  "
                >
                  {{ vehicleType.name
                  }}{{ vehicleType.active ? "" : " (inactive)" }}
                </option>
              </select>
            </label>
            <label class="text-sm text-slate-600 dark:text-slate-300">
              Quantity
              <input
                v-model.number="form.vehicle_count"
                type="number"
                min="1"
                max="999"
                class="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
              />
            </label>
          </div>
        </section>
      </main>
    </div>

    <section
      id="personnel-types"
      class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:p-6"
    >
      <div>
        <p class="text-xs uppercase tracking-wide text-slate-400">
          Personnel configuration
        </p>
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Personnel Types
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          These types are available when assigning skills to staff and defining
          unit requirements. Renaming a type updates it everywhere it is used.
        </p>
      </div>

      <div class="mt-4 grid gap-3 md:grid-cols-2">
        <div
          v-for="personnelType in skills"
          :key="personnelType.id"
          class="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700"
        >
          <input
            v-model="personnelType.editing_name"
            type="text"
            :aria-label="`Personnel type ${personnelType.name}`"
            class="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700"
            @keyup.enter="savePersonnelType(personnelType)"
          />
          <button
            type="button"
            class="btn inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap px-3"
            :disabled="savingPersonnelTypeId === personnelType.id"
            @click="savePersonnelType(personnelType)"
          >
            {{
              savingPersonnelTypeId === personnelType.id ? "Saving..." : "Save"
            }}
          </button>
        </div>
      </div>

      <div
        class="mt-4 flex flex-wrap gap-3 border-t border-slate-100 pt-4 dark:border-slate-700"
      >
        <input
          v-model="newPersonnelTypeName"
          type="text"
          placeholder="New personnel type, e.g. Doctor"
          class="min-w-[14rem] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700"
          @keyup.enter="addPersonnelType"
        />
        <button
          type="button"
          class="btn accent inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap px-4"
          :disabled="savingPersonnelTypeId === 'new'"
          @click="addPersonnelType"
        >
          <PlusIcon class="h-4 w-4" />
          {{
            savingPersonnelTypeId === "new" ? "Adding..." : "Add Personnel Type"
          }}
        </button>
      </div>
    </section>

    <section
      class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:p-6"
    >
      <div>
        <p class="text-xs uppercase tracking-wide text-slate-400">
          Fleet configuration
        </p>
        <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Vehicle Types
        </h2>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
          These types are available when classifying fleet vehicles and defining
          unit requirements.
        </p>
      </div>

      <div class="mt-4 grid gap-3 md:grid-cols-2">
        <div
          v-for="vehicleType in vehicleTypes"
          :key="vehicleType.id"
          class="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700"
        >
          <input
            v-model="vehicleType.editing_name"
            type="text"
            class="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700"
          />
          <label
            class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
          >
            <input
              v-model="vehicleType.active"
              type="checkbox"
              class="h-4 w-4 rounded border-slate-300 text-cyan-600"
            />
            Active
          </label>
          <button
            type="button"
            class="btn inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap px-3"
            :disabled="savingVehicleTypeId === vehicleType.id"
            @click="saveVehicleType(vehicleType)"
          >
            {{ savingVehicleTypeId === vehicleType.id ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>

      <div
        class="mt-4 flex flex-wrap gap-3 border-t border-slate-100 pt-4 dark:border-slate-700"
      >
        <input
          v-model="newVehicleTypeName"
          type="text"
          placeholder="New vehicle type"
          class="min-w-[14rem] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700"
          @keyup.enter="addVehicleType"
        />
        <button
          type="button"
          class="btn accent inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap px-4"
          :disabled="savingVehicleTypeId === 'new'"
          @click="addVehicleType"
        >
          <PlusIcon class="h-4 w-4" />
          {{ savingVehicleTypeId === "new" ? "Adding..." : "Add Type" }}
        </button>
      </div>
    </section>
  </div>
</template>
